import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { Bell, Car, Wrench, ArrowRightLeft, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Notification, NotificationFilters, NotificationType } from '../../../shared/types/notification.types'
import useNotification from '../wrappers/use-notification'
import { useAuth } from '../contexts/AuthContext'
import {
    getNotificationItemStyles,
    getNotificationMessageStyles,
    getNotificationReadToggleStyles,
    getNotificationTimestampStyles,
    getNotificationTitleStyles,
    getNotificationTypeIconStyles,
    notificationListStyles,
} from './NotificationList.styles'

interface NotificationListProps {
    filters: NotificationFilters
    onRead?: () => void
    onDelete?: () => void
}

const TYPE_ICON: Record<NotificationType, React.ReactNode> = {
    vehicleStatus:   <Car size={18} />,
    serviceStatus:   <Wrench size={18} />,
    vehicleTransfer: <ArrowRightLeft size={18} />,
    system:          <Bell size={18} />,
}

const NotificationList: React.FC<NotificationListProps> = ({ filters, onRead, onDelete }) => {
    const { t } = useTranslation()
    const { list, edit, remove } = useNotification()
    const { user } = useAuth()
    const navigate = useNavigate()
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [loading, setLoading] = useState(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filtersKey = JSON.stringify(filters)

    useEffect(() => {
        let cancelled = false
        const fetch = async () => {
            setLoading(true)
            try {
                const result = await list(filters)
                if (!cancelled) setNotifications(result)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        fetch()
        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey])

    const handleClick = async (n: Notification) => {
        if (!n.read) {
            await edit(n.id, { read: true, readAt: new Date(), readBy: user?.uid })
            setNotifications(prev =>
                prev.map(x => x.id === n.id ? { ...x, read: true, readAt: new Date() } : x)
            )
            onRead?.()
        }
        if (n.link) navigate(n.link)
    }

    const handleToggleRead = async (e: React.MouseEvent, n: Notification) => {
        e.stopPropagation()
        if (n.read) {
            await edit(n.id, { read: false, readAt: null, readBy: '' })
            setNotifications(prev =>
                prev.map(x => x.id === n.id ? { ...x, read: false, readAt: null } : x)
            )
        } else {
            await edit(n.id, { read: true, readAt: new Date(), readBy: user?.uid })
            setNotifications(prev =>
                prev.map(x => x.id === n.id ? { ...x, read: true, readAt: new Date() } : x)
            )
            onRead?.()
        }
    }

    const handleDelete = async (e: React.MouseEvent, n: Notification) => {
        e.stopPropagation()
        await remove(n.id)
        setNotifications(prev => prev.filter(x => x.id !== n.id))
        if (!n.read) onRead?.()
        onDelete?.()
    }

    if (loading) {
        return (
            <Box sx={notificationListStyles.loadingContainer}>
                <CircularProgress size={24} />
            </Box>
        )
    }

    if (notifications.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('notificationList.empty')}
            </Typography>
        )
    }

    return (
        <Box sx={notificationListStyles.list}>
            {notifications.map(n => (
                <Box
                    key={n.id}
                    onClick={() => handleClick(n)}
                    sx={getNotificationItemStyles(n.read, Boolean(n.link))}
                >
                    {/* Klikatelný pruh vlevo — toggle přečteno/nepřečteno */}
                    <Tooltip title={n.read ? t('notificationList.markUnread') : t('notificationList.markRead')} placement="right">
                        <Box
                            onClick={(e) => handleToggleRead(e, n)}
                            sx={getNotificationReadToggleStyles(n.read)}
                        />
                    </Tooltip>

                    {/* Ikona typu */}
                    <Box sx={getNotificationTypeIconStyles(n.read)}>
                        {TYPE_ICON[n.type]}
                    </Box>

                    {/* Obsah */}
                    <Box sx={notificationListStyles.content}>
                        <Box sx={notificationListStyles.contentHeader}>
                            <Typography variant="body2" fontWeight={n.read ? 400 : 600} noWrap sx={getNotificationTitleStyles()}>
                                {t(n.title, n.params)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={getNotificationTimestampStyles()}>
                                {n.createdAt.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                                {' '}
                                {n.createdAt.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })}
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={getNotificationMessageStyles()}>
                            {t(n.message, n.params)}
                        </Typography>
                    </Box>

                    {/* Popelnice — pravý dolní roh */}
                    <Tooltip title={t('notificationList.delete')}>
                        <IconButton
                            size="small"
                            onClick={(e) => handleDelete(e, n)}
                            sx={notificationListStyles.deleteButton}
                        >
                            <Trash2 size={14} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ))}
        </Box>
    )
}

export default NotificationList
